// Polished WhatsApp / Email templates for non-Mivent team members.
// Goals: easy to understand, complete details, clear next steps, polite tone.
// Used by Event manual-share modal and Team invite modal.

const clean = (v, fallback = "—") => {
  const s = String(v ?? "").trim();
  return s || fallback;
};

const formatDateDisplaySafe = (d) => {
  if (!d) return "TBD";
  try {
    // yyyy-mm-dd -> dd MMM yyyy for readability
    const m = String(d).match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (m) {
      const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    }
    const dt = new Date(d);
    if (!isFinite(dt.getTime())) return String(d);
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return String(d);
  }
};

// Production app link sent to team members. Never send localhost to real users.
const MIVENT_APP_URL =
  (typeof process !== "undefined" && process.env?.REACT_APP_APP_URL) || "https://miventsite.com";

// ---------------------------------------------------------------------------
// Client share templates (Quotation / Event / Bill)
// Each builder supports audience: "client" | "others".
// - "client": personalized, sent directly to the client (wa.me client phone / mailto client email)
// - "others": neutral forwardable copy, meant to be copied and pasted anywhere
//   (family member, partner, staff). Includes client reference + who shared it.
// NOTE: keep these messages emoji-free (plain text with *bold*) so they render
// correctly on all phones and mail clients.
// ---------------------------------------------------------------------------

const formatINR = (n) => {
  const amt = parseFloat(n) || 0;
  try {
    return `₹${amt.toLocaleString("en-IN")}`;
  } catch {
    return `Rs. ${amt}`;
  }
};

const companyLine = (company = {}) => {
  const parts = [];
  if (company?.name) parts.push(String(company.name));
  if (company?.phone) parts.push(`Phone: ${company.phone}`);
  if (company?.email) parts.push(`Email: ${company.email}`);
  if (company?.address) parts.push(`Address: ${company.address}`);
  return parts;
};

const companySignature = (company = {}, fallback = "Company Name") => {
  const name = String(company?.name || fallback || "Company Name").trim() || "Company Name";
  return { name, lines: companyLine(company?.name ? company : { ...company, name }) };
};

const quotationItems = (q = {}) => {
  if (Array.isArray(q.items) && q.items.length > 0) {
    return q.items.filter((it) => it && String(it.service || "").trim() !== "");
  }
  return [];
};

const quotationServicesText = (q = {}) => {
  const fromServices = Array.isArray(q.services) ? q.services.filter(Boolean).map((s) => (typeof s === "string" ? s : s?.name)).filter(Boolean) : [];
  if (fromServices.length > 0) return fromServices;
  return quotationItems(q).map((it) => it.service);
};

const eventServices = (ev = {}) => (Array.isArray(ev.services) ? ev.services.filter(Boolean) : []);

// Map link per service — works with new locationDetail or falls back to plain text.
// No API key needed: plain text becomes a Google Maps search link, lat/lng becomes an exact pin.
const serviceLocationText = (s = {}) => {
  const detail = s.locationDetail || {};
  const addr = detail.address ? String(detail.address).trim() : '';
  const plain = s.location ? String(s.location).trim() : '';
  return addr || plain;
};

const serviceMapUrl = (s = {}) => {
  const detail = s.locationDetail || {};
  if (detail.mapUrl && String(detail.mapUrl).trim()) return String(detail.mapUrl).trim();
  const lat = Number(detail.lat);
  const lng = Number(detail.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
    return `https://www.google.com/maps?q=${lat},${lng}`;
  }
  const text = serviceLocationText(s);
  if (!text) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`;
};

const invoiceItems = (inv = {}) => {
  if (Array.isArray(inv.items) && inv.items.length > 0) return inv.items.filter((it) => it && String(it.service || "").trim() !== "");
  const svcs = Array.isArray(inv.services) ? inv.services.filter(Boolean) : [];
  return svcs.map((s) => (typeof s === "string" ? { service: s, quantity: 1, price: 0 } : { service: s?.name || "Service", quantity: 1, price: 0 }));
};

// ------------------------------- QUOTATION --------------------------------

/**
 * WhatsApp text for a quotation.
 * audience "client": "Hello <name>, here is your quotation..." (send directly)
 * audience "others": "Hello, sharing quotation details..." (copy & forward)
 */
export function buildQuotationWhatsApp({ quotation = {}, company = {}, audience = "client" } = {}) {
  const q = quotation || {};
  const { name: coName, lines: coLines } = companySignature(company);
  const client = String(q.clientName || "").trim() || "there";
  const qid = String(q.id || q._id || "").trim() || "N/A";
  const eventDate = q.mainEventDate ? formatDateDisplaySafe(q.mainEventDate) : (q.date ? formatDateDisplaySafe(q.date) : "TBD");
  const items = quotationItems(q);
  const total = formatINR(q.totalAmount);
  const lines = [];

  if (audience === "others") {
    lines.push(`Hello, sharing quotation details from *${coName}*:`);
    lines.push(``);
    lines.push(`Client: ${String(q.clientName || "—")}${q.clientPhone ? ` (${q.clientPhone})` : ""}`);
  } else {
    lines.push(`Hello ${client},`);
    lines.push(``);
    lines.push(`Thank you for choosing *${coName}*! Here is your event quotation.`);
  }
  lines.push(``);
  lines.push(`*Quotation ${qid}*`);
  lines.push(`- Event Date: ${eventDate}`);
  if (items.length > 0) {
    lines.push(`- Services (${items.length}):`);
    items.slice(0, 8).forEach((it, i) => {
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.price) || 0;
      const lineTotal = qty && price ? ` - ${formatINR(qty * price)}` : price ? ` - ${formatINR(price)}` : "";
      lines.push(`  ${i + 1}. ${it.service}${qty > 1 ? ` x ${qty}` : ""}${lineTotal}`);
    });
    if (items.length > 8) lines.push(`  + ${items.length - 8} more (see full quotation)`);
  } else {
    const svcs = quotationServicesText(q);
    if (svcs.length > 0) lines.push(`- Services: ${svcs.slice(0, 6).join(", ")}${svcs.length > 6 ? ` +${svcs.length - 6} more` : ""}`);
  }
  lines.push(`- *Total: ${total}*`);
  lines.push(``);
  if (audience === "others") {
    lines.push(`Please review and share your confirmation with the client / team.`);
    lines.push(`For questions, contact ${coName}${company?.phone ? ` at ${company.phone}` : ""}.`);
  } else {
    lines.push(`Please review and reply to confirm, or let us know if you need any changes.`);
  }
  lines.push(``);
  lines.push(`Regards,`);
  lines.push(...coLines);
  return lines.join("\n");
}

/**
 * Email subject + body for a quotation.
 */
export function buildQuotationEmail({ quotation = {}, company = {}, audience = "client" } = {}) {
  const q = quotation || {};
  const { name: coName, lines: coLines } = companySignature(company);
  const client = String(q.clientName || "").trim() || "there";
  const qid = String(q.id || q._id || "").trim() || "";
  const eventDate = q.mainEventDate ? formatDateDisplaySafe(q.mainEventDate) : (q.date ? formatDateDisplaySafe(q.date) : "TBD");
  const items = quotationItems(q);
  const total = formatINR(q.totalAmount);

  const subject =
    audience === "others"
      ? `Quotation ${qid} - ${q.clientName || "Client"} | ${total} (shared via ${coName})`
      : `${q.clientName || "Client"} - Your Quotation ${qid} | ${total}`;

  const out = [];
  if (audience === "others") {
    out.push(`Hi,`);
    out.push(``);
    out.push(`I'm sharing the quotation below from ${coName} for your reference / follow-up:`);
  } else {
    out.push(`Hello ${client},`);
    out.push(``);
    out.push(`Thank you for choosing ${coName}! Please find your quotation details below:`);
  }
  out.push(``);
  out.push(`QUOTATION SUMMARY`);
  out.push(`------------------------------`);
  if (qid) out.push(`Quotation : ${qid}`);
  out.push(`Client    : ${String(q.clientName || "—")}`);
  if (q.clientPhone) out.push(`Phone     : ${q.clientPhone}`);
  if (q.clientEmail) out.push(`Email     : ${q.clientEmail}`);
  out.push(`Event Date: ${eventDate}`);
  out.push(``);
  if (items.length > 0) {
    out.push(`SERVICES (${items.length})`);
    out.push(`------------------------------`);
    items.forEach((it, i) => {
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.price) || 0;
      out.push(`${i + 1}. ${it.service}${qty ? `  (Qty: ${qty})` : ""}${price ? `  @ ${formatINR(price)}` : ""}`);
      if (it.description) out.push(`   ${String(it.description).slice(0, 200)}`);
    });
    out.push(``);
  }
  out.push(`Total Amount: ${total}`);
  out.push(``);
  out.push(`WHAT TO DO NEXT`);
  out.push(`------------------------------`);
  if (audience === "others") {
    out.push(`Please review and pass your confirmation / changes to the concerned person.`);
  } else {
    out.push(`1. Review the services and total above.`);
    out.push(`2. Reply to this email (or WhatsApp us) to confirm or request changes.`);
  }
  out.push(``);
  out.push(`Regards,`);
  out.push(...coLines);
  return { subject, body: out.join("\n") };
}

// --------------------------------- EVENT ----------------------------------

export function buildEventWhatsApp({ event = {}, company = {}, audience = "client" } = {}) {
  const ev = event || {};
  const { name: coName, lines: coLines } = companySignature(company);
  const client = String(ev.clientName || "").trim() || "there";
  const eid = String(ev.id || ev._id || ev.quotationId || "").trim() || "N/A";
  const services = eventServices(ev);
  const lines = [];

  if (audience === "others") {
    lines.push(`Hello, sharing event details from *${coName}*:`);
    lines.push(``);
    lines.push(`Client: ${String(ev.clientName || "—")}${ev.clientPhone ? ` (${ev.clientPhone})` : ""}`);
  } else {
    lines.push(`Hello ${client},`);
    lines.push(``);
    lines.push(`Here is an update on your event with *${coName}*.`);
  }
  lines.push(``);
  lines.push(`*Event ${eid}*`);
  if (services.length > 0) {
    lines.push(`- Services (${services.length}):`);
    services.slice(0, 8).forEach((s, i) => {
      const nm = s?.name || `Service ${i + 1}`;
      const dt = s?.date ? formatDateDisplaySafe(s.date) : "";
      const tm = s?.time ? String(s.time).trim() : "";
      const loc = serviceLocationText(s);
      const map = serviceMapUrl(s);
      const when = [dt, tm].filter(Boolean).join(" ").trim();
      lines.push(`  ${i + 1}. *${nm}*${when ? ` - ${when}` : ""}${loc ? ` @ ${loc}` : ""}`);
      if (map && loc) lines.push(`     Map: ${map}`);
    });
    if (services.length > 8) lines.push(`  + ${services.length - 8} more`);
  } else {
    if (ev.date) lines.push(`- Date: ${formatDateDisplaySafe(ev.date)}`);
    if (ev.time) lines.push(`- Time: ${ev.time}`);
    if (ev.location) lines.push(`- Venue: ${ev.location}`);
  }
  if (ev.notes) lines.push(`- Note: ${String(ev.notes).slice(0, 250)}`);
  lines.push(``);
  if (audience === "others") {
    lines.push(`Shared by ${coName} - for info / coordination. For questions, contact us directly.`);
  } else {
    lines.push(`Please contact us if you have any questions or need changes.`);
  }
  lines.push(``);
  lines.push(`Regards,`);
  lines.push(...coLines);
  return lines.join("\n");
}

export function buildEventEmail({ event = {}, company = {}, audience = "client" } = {}) {
  const ev = event || {};
  const { name: coName, lines: coLines } = companySignature(company);
  const client = String(ev.clientName || "").trim() || "there";
  const eid = String(ev.id || ev._id || ev.quotationId || "").trim() || "";
  const services = eventServices(ev);

  const subject =
    audience === "others"
      ? `Event ${eid} - ${ev.clientName || "Client"} (shared via ${coName})`
      : `${ev.clientName || "Client"} - Your Event Update ${eid}`;

  const out = [];
  if (audience === "others") {
    out.push(`Hi,`);
    out.push(``);
    out.push(`I'm sharing the event details below from ${coName}:`);
  } else {
    out.push(`Hello ${client},`);
    out.push(``);
    out.push(`Here is an update on your event with ${coName}:`);
  }
  out.push(``);
  out.push(`EVENT SUMMARY`);
  out.push(`------------------------------`);
  if (eid) out.push(`Event  : ${eid}`);
  out.push(`Client : ${String(ev.clientName || "—")}`);
  if (ev.clientPhone) out.push(`Phone  : ${ev.clientPhone}`);
  if (ev.clientEmail) out.push(`Email  : ${ev.clientEmail}`);
  out.push(``);
  if (services.length > 0) {
    out.push(`SERVICES (${services.length})`);
    out.push(`------------------------------`);
    services.forEach((s, i) => {
      const nm = s?.name || `Service ${i + 1}`;
      const dt = s?.date ? formatDateDisplaySafe(s.date) : "TBD";
      const tm = s?.time ? String(s.time).trim() : "—";
      const loc = serviceLocationText(s) || "—";
      const map = serviceMapUrl(s);
      out.push(`${i + 1}. ${nm}`);
      out.push(`   Date: ${dt} | Time: ${tm} | Venue: ${loc}`);
      if (map && loc !== "—") out.push(`   Map: ${map}`);
      if (s?.notes) out.push(`   Note: ${String(s.notes).slice(0, 200)}`);
    });
    out.push(``);
  }
  if (ev.notes) {
    out.push(`NOTES`);
    out.push(`------------------------------`);
    out.push(String(ev.notes).slice(0, 800));
    out.push(``);
  }
  out.push(audience === "others" ? `For questions, please contact ${coName} directly.` : `Please reply to this email if you have any questions.`);
  out.push(``);
  out.push(`Regards,`);
  out.push(...coLines);
  return { subject, body: out.join("\n") };
}

// ---------------------------------- BILL ----------------------------------

export function buildInvoiceWhatsApp({ invoice = {}, company = {}, audience = "client" } = {}) {
  const inv = invoice || {};
  const { name: coName, lines: coLines } = companySignature(company);
  const client = String(inv.clientName || "").trim() || "there";
  const iid = String(inv.id || inv._id || "").trim() || "N/A";
  const amount = formatINR(inv.amount ?? inv.totalAmount);
  const paid = parseFloat(inv.paid) || 0;
  const balance = amount && paid ? formatINR((parseFloat(inv.amount) || 0) - paid) : null;
  const items = invoiceItems(inv);
  const lines = [];

  if (audience === "others") {
    lines.push(`Hello, sharing invoice details from *${coName}*:`);
    lines.push(``);
    lines.push(`Client: ${String(inv.clientName || "—")}${inv.clientPhone ? ` (${inv.clientPhone})` : ""}`);
  } else {
    lines.push(`Hello ${client},`);
    lines.push(``);
    lines.push(`This is regarding your invoice from *${coName}*.`);
  }
  lines.push(``);
  lines.push(`*Invoice ${iid}*`);
  if (items.length > 0) {
    lines.push(`- Items: ${items.slice(0, 5).map((it) => it.service).join(", ")}${items.length > 5 ? ` +${items.length - 5} more` : ""}`);
  }
  lines.push(`- *Amount: ${amount}*`);
  if (paid > 0) lines.push(`- Paid: ${formatINR(paid)}`);
  if (balance) lines.push(`- *Balance due: ${balance}*`);
  if (inv.status) lines.push(`- Status: ${String(inv.status)}`);
  lines.push(``);
  if (audience === "others") {
    lines.push(`Shared by ${coName} - please coordinate payment / follow-up as needed.`);
  } else {
    lines.push(`Please let us know once done, or contact us if you have any questions about this bill.`);
  }
  lines.push(``);
  lines.push(`Regards,`);
  lines.push(...coLines);
  return lines.join("\n");
}

export function buildInvoiceEmail({ invoice = {}, company = {}, audience = "client" } = {}) {
  const inv = invoice || {};
  const { name: coName, lines: coLines } = companySignature(company);
  const client = String(inv.clientName || "").trim() || "there";
  const iid = String(inv.id || inv._id || "").trim() || "";
  const amount = formatINR(inv.amount ?? inv.totalAmount);
  const paid = parseFloat(inv.paid) || 0;
  const balance = formatINR((parseFloat(inv.amount) || 0) - paid);
  const items = invoiceItems(inv);

  const subject =
    audience === "others"
      ? `Invoice ${iid} - ${inv.clientName || "Client"} | ${amount} (shared via ${coName})`
      : `${inv.clientName || "Client"} - Invoice ${iid} | Amount ${amount}`;

  const out = [];
  if (audience === "others") {
    out.push(`Hi,`);
    out.push(``);
    out.push(`I'm sharing the invoice below from ${coName} for your reference / follow-up:`);
  } else {
    out.push(`Hello ${client},`);
    out.push(``);
    out.push(`Please find your invoice details from ${coName} below:`);
  }
  out.push(``);
  out.push(`INVOICE SUMMARY`);
  out.push(`------------------------------`);
  if (iid) out.push(`Invoice : ${iid}`);
  out.push(`Client  : ${String(inv.clientName || "—")}`);
  if (inv.clientPhone) out.push(`Phone   : ${inv.clientPhone}`);
  if (inv.clientEmail) out.push(`Email   : ${inv.clientEmail}`);
  if (inv.issueDate || inv.date) out.push(`Date    : ${formatDateDisplaySafe(inv.issueDate || inv.date)}`);
  if (inv.status) out.push(`Status  : ${inv.status}`);
  out.push(``);
  if (items.length > 0) {
    out.push(`ITEMS (${items.length})`);
    out.push(`------------------------------`);
    items.forEach((it, i) => {
      const qty = parseFloat(it.quantity) || 0;
      const price = parseFloat(it.price) || 0;
      out.push(`${i + 1}. ${it.service}${qty ? `  (Qty: ${qty})` : ""}${price ? `  @ ${formatINR(price)}` : ""}`);
    });
    out.push(``);
  }
  out.push(`Amount      : ${amount}`);
  if (paid > 0) {
    out.push(`Paid        : ${formatINR(paid)}`);
    out.push(`Balance due : ${balance}`);
  }
  out.push(``);
  out.push(audience === "others" ? `Please coordinate payment / confirmation as needed. For questions, contact ${coName} directly.` : `Please reply to this email once paid, or contact us if you have any questions.`);
  out.push(``);
  out.push(`Regards,`);
  out.push(...coLines);
  return { subject, body: out.join("\n") };
}

export function getAppOrigin() {
  try {
    if (typeof window !== "undefined" && window.location?.origin) {
      const origin = window.location.origin;
      // On local dev use production URL, otherwise the member
      // would get an unusable http://localhost:3000 link.
      if (/localhost|127\.0\.0\.1|192\.168\./i.test(origin)) return MIVENT_APP_URL;
      return origin;
    }
  } catch {}
  return MIVENT_APP_URL;
}

/**
 * Build WhatsApp assignment message (plain text with *bold*, no emojis
 * so it renders correctly on all phones).
 * Keep it short enough for wa.me but complete: greeting, who assigned,
 * event summary (ID/client/role only), services list with date/time/venue,
 * notes, what-to-do-next, Mivent invite.
 */
export function buildAssignmentWhatsApp({ memberName, memberRole, event = {}, assignerName = "", assignerPhone = "" }) {
  const ev = event || {};
  const name = clean(memberName, "there");
  const owner = clean(assignerName, "Your coordinator");
  const eventId = clean(ev.id || ev.quotationId, "N/A");
  const client = clean(ev.clientName, "—");
  const role = clean(memberRole, "");
  const notes = String(ev.notes || "").trim();
  const services = Array.isArray(ev.services) ? ev.services.filter(Boolean) : [];

  const lines = [];
  lines.push(`Hello ${name},`);
  lines.push(``);
  lines.push(`You have been assigned to an event by *${owner}* via *Mivent*.`);
  lines.push(``);
  lines.push(`*Event Details*`);
  lines.push(`- Event ID: ${eventId}`);
  lines.push(`- Client: ${client}`);
  if (role && role !== "—") lines.push(`- Your role: ${role}`);
  // NOTE: date / time / venue intentionally NOT duplicated here —
  // each service below already carries its own date, time & venue.
  // Fallback only when event has no services list.
  if (services.length === 0) {
    const date = formatDateDisplaySafe(ev.date);
    const time = clean(ev.time, "");
    const venue = clean(ev.location, "");
    if (date && date !== "TBD") lines.push(`- Date: ${date}`);
    if (time && time !== "—") lines.push(`- Time: ${time}`);
    if (venue && venue !== "—") lines.push(`- Venue: ${venue}`);
  }
  lines.push(``);

  if (services.length > 0) {
    lines.push(`*Your Services (${services.length})*`);
    services.slice(0, 6).forEach((s, i) => {
      const sName = clean(s?.name, `Service ${i + 1}`);
      const sDate = s?.date ? formatDateDisplaySafe(s.date) : "";
      const sTime = s?.time ? String(s.time).trim() : "";
      const sLoc = serviceLocationText(s);
      const sMap = serviceMapUrl(s);
      const extra = [sDate, sTime].filter(Boolean).join(" ").trim();
      lines.push(`${i + 1}. *${sName}*${extra ? ` - ${extra}` : ""}${sLoc ? ` @ ${sLoc}` : ""}`);
      if (sMap && sLoc) lines.push(`   Map: ${sMap}`);
    });
    if (services.length > 6) lines.push(`+ ${services.length - 6} more - please confirm for full list.`);
    lines.push(``);
  }

  if (notes) {
    const short = notes.length > 280 ? `${notes.slice(0, 280)}...` : notes;
    lines.push(`*Important notes:*`);
    lines.push(short);
    lines.push(``);
  }

  lines.push(`*What to do next:*`);
  lines.push(`1. Reply to this message to confirm you are available`);
  lines.push(`2. Reach the venue 30 mins early`);
  if (assignerPhone) lines.push(`3. For any change, call ${owner}: ${assignerPhone}`);
  else lines.push(`3. For any change, contact ${owner} directly`);
  lines.push(``);
  lines.push(`You got this on WhatsApp because you are not yet on Mivent. Join FREE with your email to get automatic event alerts and payment updates:`);
  const origin = getAppOrigin();
  lines.push(origin ? `${origin} - sign up with this same email/phone` : `Ask ${owner} for the Mivent invite link`);
  lines.push(``);
  lines.push(`Thank you!`);
  lines.push(`- ${owner} (via Mivent)`);

  return lines.join("\n");
}

/**
 * Build Email assignment: { subject, body }.
 * Subject is scannable; body is formal with clear sections.
 */
export function buildAssignmentEmail({ memberName, memberRole, event = {}, assignerName = "", assignerEmail = "", assignerPhone = "" }) {
  const ev = event || {};
  const name = clean(memberName, "there");
  const owner = clean(assignerName, "Your coordinator");
  const eventId = clean(ev.id || ev.quotationId, "Mivent Event");
  const client = clean(ev.clientName, "—");
  const role = clean(memberRole, "");
  const notes = String(ev.notes || "").trim();
  const services = Array.isArray(ev.services) ? ev.services.filter(Boolean) : [];
  const clientPhone = String(ev.clientPhone || "").trim();
  const clientEmail = String(ev.clientEmail || "").trim();

  const subject = `You're assigned: ${eventId} — ${client} (by ${owner})`;

  const out = [];
  out.push(`Hi ${name},`);
  out.push(``);
  out.push(`Good news — ${owner} has assigned you to an event on Mivent. Please find your details below:`);
  out.push(``);
  out.push(`EVENT SUMMARY`);
  out.push(`------------------------------`);
  out.push(`Event ID   : ${eventId}`);
  out.push(`Client     : ${client}`);
  if (clientPhone) out.push(`Client ph. : ${clientPhone}`);
  if (clientEmail) out.push(`Client em. : ${clientEmail}`);
  // NOTE: date / time / venue intentionally NOT duplicated here —
  // each service below already carries its own date, time & venue.
  if (services.length === 0) {
    const date = formatDateDisplaySafe(ev.date);
    const time = clean(ev.time, "");
    const venue = clean(ev.location, "");
    if (date && date !== "TBD") out.push(`Date       : ${date}`);
    if (time && time !== "—") out.push(`Time       : ${time}`);
    if (venue && venue !== "—") out.push(`Venue      : ${venue}`);
  }
  if (role && role !== "—") out.push(`Your role  : ${role}`);
  out.push(`Assigned by: ${owner}${assignerPhone ? ` (${assignerPhone})` : ""}${assignerEmail ? ` <${assignerEmail}>` : ""}`);
  out.push(``);

  if (services.length > 0) {
    out.push(`SERVICES (${services.length})`);
    out.push(`------------------------------`);
    services.forEach((s, i) => {
      const sName = clean(s?.name, `Service ${i + 1}`);
      const sDate = s?.date ? formatDateDisplaySafe(s.date) : "TBD";
      const sTime = s?.time ? String(s.time).trim() : "—";
      const sLoc = serviceLocationText(s) || "—";
      const sMap = serviceMapUrl(s);
      out.push(`${i + 1}. ${sName}`);
      out.push(`   Date: ${sDate} | Time: ${sTime} | Venue: ${sLoc}`);
      if (sMap && sLoc !== "—") out.push(`   Map: ${sMap}`);
      if (s?.notes) out.push(`   Note: ${String(s.notes).slice(0, 200)}`);
    });
    out.push(``);
  }

  if (notes) {
    out.push(`IMPORTANT NOTES FROM COORDINATOR`);
    out.push(`------------------------------`);
    out.push(notes.slice(0, 800));
    out.push(``);
  }

  out.push(`WHAT TO DO NEXT`);
  out.push(`------------------------------`);
  out.push(`1. Reply to this email (or WhatsApp your coordinator) to confirm availability.`);
  out.push(`2. Reach the venue 30 minutes early with your equipment/outfit as discussed.`);
  out.push(`3. If anything changes, contact ${owner}${assignerPhone ? ` at ${assignerPhone}` : ""} immediately.`);
  out.push(``);
  out.push(`You are receiving this email because you are not yet registered on Mivent with this email address.`);
  out.push(`Join Mivent FREE using this same email to get automatic event alerts, schedules and payment updates.`);
  const origin = getAppOrigin();
  if (origin) {
    out.push(`Sign up here: ${origin}`);
  }
  out.push(``);
  out.push(`Thank you for being part of the team!`);
  out.push(``);
  out.push(`Warm regards,`);
  out.push(`${owner}`);
  out.push(`(Sent via Mivent)`);

  return { subject, body: out.join("\n") };
}

/**
 * Team invite (no event yet) — when a new team member is created and
 * they are NOT a Mivent user. Short, welcoming, tells them what happens next.
 */
export function buildTeamInviteWhatsApp({ memberName, memberRole, ownerName = "" }) {
  const name = clean(memberName, "there");
  const owner = clean(ownerName, "Your coordinator");
  const role = clean(memberRole, "team member");
  const origin = getAppOrigin();
  return [
    `Hello ${name},`,
    ``,
    `*${owner}* has added you as *${role}* in their team on *Mivent*.`,
    ``,
    `What is Mivent? It's a simple app where you will get:`,
    `- Event assignments with date, time and venue`,
    `- Service details and important notes`,
    `- Payment updates for your work`,
    ``,
    `No action needed now - you will get event details on WhatsApp/Email.`,
    `For faster auto-alerts, join FREE on Mivent with this same phone/email${origin ? `: ${origin}` : `.`}`,
    ``,
    `Reply here to confirm you received this. Thank you!`,
    `- ${owner} (via Mivent)`,
  ].join("\n");
}

export function buildTeamInviteEmail({ memberName, memberRole, ownerName = "", ownerEmail = "", ownerPhone = "" }) {
  const name = clean(memberName, "there");
  const owner = clean(ownerName, "Your coordinator");
  const role = clean(memberRole, "team member");
  const origin = getAppOrigin();
  const subject = `${owner} added you as ${role} on Mivent - no action needed`;
  const body = [
    `Hi ${name},`,
    ``,
    `${owner} has added you as "${role}" in their team on Mivent.`,
    ``,
    `WHAT THIS MEANS FOR YOU`,
    `------------------------------`,
    `- You will now receive event assignments (date, time, venue, services) on WhatsApp/Email.`,
    `- Just reply to confirm availability for each event.`,
    `- Your payments for events will be tracked clearly.`,
    ``,
    `HOW TO GET AUTO-ALERTS (OPTIONAL BUT RECOMMENDED)`,
    `------------------------------`,
    `Join Mivent FREE using this same email address to see all your events & payments in one place.`,
    origin ? `Sign up here: ${origin}` : ``,
    ``,
    `If you have questions, contact ${owner}${ownerPhone ? ` at ${ownerPhone}` : ""}${ownerEmail ? ` (${ownerEmail})` : ""}.`,
    ``,
    `Welcome aboard!`,
    ``,
    `Warm regards,`,
    `${owner}`,
    `(Sent via Mivent)`,
  ].filter((l) => l !== null).join("\n");
  return { subject, body };
}
